import { query, request } from './client'
import type {
  Challenge,
  CompletedPart,
  CompleteUploadResult,
  CreateUploadResult,
  EventDetails,
  Guest,
  GuestProfile,
  MediaDetail,
  MediaKind,
  MediaPage,
  UploadPartsResult,
} from './types'

/** De filters van de drie tabs boven de galerij. */
export interface GalleryFilter {
  guestId?: string
  kind?: MediaKind

  /** Alleen wat bij deze challenge ingeleverd is (ronde 22E). */
  challengeId?: string
}

export function getEvent(slug: string, signal?: AbortSignal): Promise<EventDetails> {
  return request<EventDetails>(`/api/events/${slug}`, { slug, signal })
}

export function registerGuest(
  slug: string,
  body: {
    guestId?: string
    displayName: string
    tableNumber?: number

    /**
     * Neem de naam uit de eigenaarssessie in plaats van uit `displayName`.
     * De server leest hem uit de cookie; wat hier in `displayName` staat doet
     * er dan niet toe, want anders was het geen bewijs van wie je bent.
     */
    useOwnerAccount?: boolean
  },
): Promise<Guest> {
  return request<Guest>(`/api/events/${slug}/guests`, { slug, method: 'POST', body })
}

export function getGuestProfile(
  slug: string,
  guestId: string,
  signal?: AbortSignal,
): Promise<GuestProfile> {
  return request<GuestProfile>(`/api/events/${slug}/guests/${guestId}`, { slug, signal })
}

export function getChallenges(
  slug: string,
  guestId: string | undefined,
  signal?: AbortSignal,
): Promise<{ challenges: Challenge[] }> {
  return request<{ challenges: Challenge[] }>(
    `/api/events/${slug}/challenges${query({ guestId })}`,
    { slug, signal },
  )
}

export function getMediaPage(
  slug: string,
  filter: GalleryFilter,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<MediaPage> {
  return request<MediaPage>(`/api/events/${slug}/media${query({ ...filter, cursor, limit: 60 })}`, {
    slug,
    signal,
  })
}

export function getMediaDetail(
  slug: string,
  mediaId: string,
  filter: GalleryFilter,
  signal?: AbortSignal,
): Promise<MediaDetail> {
  return request<MediaDetail>(`/api/media/${mediaId}${query({ ...filter })}`, { slug, signal })
}

export function updateMedia(
  slug: string,
  mediaId: string,
  body: { guestId: string; challengeId: string | null },
): Promise<{ id: string; challengeId: string | null }> {
  return request(`/api/media/${mediaId}`, { slug, method: 'PATCH', body })
}

export function deleteMedia(slug: string, mediaId: string, guestId: string): Promise<void> {
  return request<void>(`/api/media/${mediaId}${query({ guestId })}`, {
    slug,
    method: 'DELETE',
  })
}

export interface CreateUploadBody {
  guestId: string
  fileName: string
  contentType: string
  sizeBytes: number
  kind: MediaKind
  challengeId?: string | null
  capturedAtUtc?: string | null
  width?: number | null
  height?: number | null
  durationSeconds?: number | null
}

export function createUpload(slug: string, body: CreateUploadBody): Promise<CreateUploadResult> {
  return request<CreateUploadResult>(`/api/events/${slug}/uploads`, {
    slug,
    method: 'POST',
    body,
  })
}

export function completeUpload(
  slug: string,
  mediaItemId: string,
  body: { width?: number | null; height?: number | null; durationSeconds?: number | null },
): Promise<CompleteUploadResult> {
  return request<CompleteUploadResult>(`/api/media/${mediaItemId}/complete`, {
    slug,
    method: 'POST',
    body,
  })
}

/**
 * Verse presigned URLs voor een upload die al bestaat. Nodig na een hervatting:
 * een handtekening leeft dertig minuten, en een nieuwe upload aanvragen zou bij
 * multipart alle parts weggooien die al gelukt waren.
 */
export function getUploadPlan(slug: string, mediaItemId: string): Promise<CreateUploadResult> {
  return request<CreateUploadResult>(`/api/media/${mediaItemId}/upload`, { slug })
}

export function getUploadParts(
  slug: string,
  mediaItemId: string,
  from: number,
  count: number,
): Promise<UploadPartsResult> {
  return request<UploadPartsResult>(
    `/api/media/${mediaItemId}/multipart/parts${query({ from, count })}`,
    { slug },
  )
}

export function completeMultipart(
  slug: string,
  mediaItemId: string,
  parts: CompletedPart[],
): Promise<void> {
  return request<void>(`/api/media/${mediaItemId}/multipart/complete`, {
    slug,
    method: 'POST',
    body: { parts },
  })
}

export function abortMultipart(slug: string, mediaItemId: string): Promise<void> {
  return request<void>(`/api/media/${mediaItemId}/multipart/abort`, {
    slug,
    method: 'POST',
    body: {},
  })
}
